import type { AxiosInstance } from 'axios'
import type { IFactoryFunctionRequest } from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'
import useAxios from '@/services/api/useAxios'
import { afterReceiving, beforeSending } from '@/services/evaluation/hooks'
import { afterReceiving as afterReceivingResource } from '@/services/resource/hooks'

const resource = 'evaluations'

type TItems = 'title' | 'question' | 'smallText' | 'blank' | 'comment' | 'input' | 'rating' | 'abcd' | 'table' | 'boolean' | 'textCheckbox' | 'ranking'

type TShowEvaluation = {
  _id: string
  startDate: string
  endDate: string
  display: string
  formAfter: {
    _id: string
    column: [{
      _id: string
      type: TItems
      display: string | null
      displayArray: any[]
      value: any
      buttons: number
      calcId: string
      averageIds: string[]
      bgColor: string
      color: string
      required: boolean
      showNA: boolean
      hideable: boolean
      hiddenBy: string
      rows: number
    }]
  }[]
  form: {
    _id: string
    column: [{
      _id: string
      type: TItems
      display: string | null
      displayArray: any[]
      value: any
      buttons: number
      calcId: string
      averageIds: string[]
      bgColor: string
      color: string
      required: boolean
      showNA: boolean
      hideable: boolean
      hiddenBy: string
      rows: number
    }]
  }[]
}
const showFillEvaluation = (axios: IFactoryFunctionRequest<TShowEvaluation, undefined>) => ({ evaluationId }: { evaluationId: string }) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/fill/${evaluationId}`,
    afterReceivingHook: afterReceiving,
  })
}

const showEditEvaluation = (axios: IFactoryFunctionRequest<TShowEvaluation, undefined>) => ({ evaluationId }: { evaluationId: string }) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/edit/${evaluationId}`,
    afterReceivingHook: afterReceiving,
  })
}

const showAverageSummary = (axios: IFactoryFunctionRequest<any, undefined>) => ({ evaluationId }: { evaluationId: string }) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${evaluationId}/average-summary`,
  })
}

const showFillEvaluationExternal = (axios: IFactoryFunctionRequest<TShowEvaluation, undefined>) => ({ evaluationId, email }: { evaluationId: string; email: string }) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/fill/external/${evaluationId}`,
    query: { email },
    afterReceivingHook: afterReceiving,
  })
}
const showEvaluationList = (axios: IFactoryFunctionRequest<TShowEvaluation, undefined>) => (query: { limit: number; page: number; search?: string; type?: any; isClosed?: boolean }) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/`,
    query,
    afterReceivingHook: afterReceiving,
  })
}
const retreiveCreateResources = (axios: IFactoryFunctionRequest<{ _id: string; firstname: string; lastname: string }[], undefined>) => (query: { resourceIds: string[] }) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/retreive/create/resources`,
    data: query,
    afterReceivingHook:afterReceivingResource
  })
}

const createEvaluations = (axios: IFactoryFunctionRequest<any, any>) => (evaluations: any[]) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/`,
    data: evaluations,
    beforeSendingHook: beforeSending,
    afterReceivingHook: afterReceiving,
  })
}

const duplicateEvaluation = (axios: IFactoryFunctionRequest<any, { resourceId: string; managedBy: string[] }>) => (
  evaluationId: string,
  data: { resourceId: string; managedBy: string[] },
) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${evaluationId}/duplicate`,
    data,
    afterReceivingHook: afterReceiving,
  })
}

const sendReminders = (axios: IFactoryFunctionRequest<any, any>) => (evaluations: any[]) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/sendReminder`,
    data: evaluations,
  })
}

const sendBulkReminders = (axios: IFactoryFunctionRequest<any, any>) => (evaluationIds: string[]) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/sendBulkReminders`,
    data: { evaluationIds },
  })
}

const sendLinktoShared = (axios: IFactoryFunctionRequest<any, any>) => ({ evaluationId, resourceId }: { evaluationId: string; resourceId: string }) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/evaluation/${evaluationId}/shared-with/${resourceId}/send-link`,
  })
}

const updateEvaluation = (axios: IFactoryFunctionRequest<any, undefined>) => ({ evaluationId, data, sendNotification = false, notificationTarget, isExternal = false, persistSkills = false, persistSkillsAfter = false }: { isExternal?: boolean; evaluationId: string; data: any; sendNotification?: boolean; notificationTarget?: 'evaluators' | 'evaluatee'; persistSkills?: boolean; persistSkillsAfter?: boolean }) => {
  const url = isExternal ? `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/update/external/${evaluationId}` : `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/update/${evaluationId}`
  return axios({
    method: EMethod.put,
    url,
    data,
    query: { sendNotification, notificationTarget, persistSkills, persistSkillsAfter },
    beforeSendingHook: beforeSending,
    afterReceivingHook: afterReceiving,
  })
}
const exportEvaluationsToExel = (axios: IFactoryFunctionRequest<IResource, any >) => (query) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/export`,
    query,
    immediate: false,
  })
}

const exportEvaluationsCountByPersonToExcel = (axios: IFactoryFunctionRequest<IResource, any >) => (query) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/export/count-by-person`,
    query,
    immediate: false,
  })
}

const retreiveAllQuestionIdAvailable = (axios: IFactoryFunctionRequest<IResource, any >) => (query: { startDate: Date; endDate: Date }) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/retreiveAllQuestionIdAvailable`,
    query,
  })
}
const retreiveStats = (axios: IFactoryFunctionRequest<any, any >) => (query: { startDate: Date; endDate: Date; ids: string[] }) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/stats`,
    query,
  })
}
const removeFromTrainingPlan = (axios: IFactoryFunctionRequest<any, any >) => (evaluationId: string, trainingId: string) => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/evaluation/${evaluationId}/trainingPlan/${trainingId}`,
    afterReceivingHook: afterReceiving,
  })
}

const reorderTrainingPlan = (axios: IFactoryFunctionRequest<any, any >) => (evaluationId: string, orderedIds: string[]) => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/evaluation/${evaluationId}/trainingPlan/reorder`,
    data: { orderedIds },
  })
}

const addToTrainingPlan = (axios: IFactoryFunctionRequest<any, any >) => (evaluationId: string, trainingId: string) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/evaluation/${evaluationId}/addTotrainingPlan/${trainingId}`,
  })
}
const getLearningPathWishes = (axios: IFactoryFunctionRequest<any, any >) => (evaluationId: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/evaluation/${evaluationId}/learningPaths`,
  })
}

const addLearningPathWish = (axios: IFactoryFunctionRequest<any, any >) => (evaluationId: string, learningPathId: string) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/evaluation/${evaluationId}/learningPaths/${learningPathId}`,
  })
}

const removeLearningPathWish = (axios: IFactoryFunctionRequest<any, any >) => (evaluationId: string, learningPathId: string) => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/evaluation/${evaluationId}/learningPaths/${learningPathId}`,
  })
}

const giveupEvaluations = (axios: IFactoryFunctionRequest<any, any >) => ({ evaluationIds, justification }: { evaluationIds: string[]; justification?: string }) => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/update/giveup`,
    data: { evaluationIds, justification },
    afterReceivingHook: afterReceiving,
  })
}
const deleteEvaluations = (axios: IFactoryFunctionRequest<any, any >) => (evaluationIds: string[]) => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/`,
    data: {evaluationIds},
  })
}

const undoGiveupEvaluations = (axios: IFactoryFunctionRequest<any, any >) => (evaluationIds: string[]) => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/update/undo-giveup`,
    data: { evaluationIds },
    afterReceivingHook: afterReceiving,
  })
}

const undoDeleteEvaluations = (axios: IFactoryFunctionRequest<any, any >) => (evaluationIds: string[]) => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/update/undo-delete`,
    data: { evaluationIds },
  })
}

const downloadPDF = (axios: IFactoryFunctionRequest<any, any >) => ({ evaluationId }: { evaluationId: string }) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/pdf/${evaluationId}`,
  })
}

const downloadMarkdown = (axios: IFactoryFunctionRequest<{ markdown: string; fileName: string }, any >) => ({ evaluationId }: { evaluationId: string }) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/markdown/${evaluationId}`,
  })
}

const detectPdfLanguages = (axios: IFactoryFunctionRequest<{ detectedLanguages: { code: string; count: number }[]; translatableItemCount: number }, any >) => ({ evaluationId }: { evaluationId: string }) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/pdf/${evaluationId}/detect-languages`,
  })
}

const downloadTranslatedPDF = (axios: IFactoryFunctionRequest<any, any >) => ({ evaluationId, targetLanguage }: { evaluationId: string; targetLanguage: string }) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/pdf/${evaluationId}/translate`,
    data: { targetLanguage },
  })
}


const globalSearch = (axios: IFactoryFunctionRequest<any, any>) => (searchString = '') => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/search`,
    query: { searchString },
    immediate: false,
  })
}

const countOpenByResource = (axios: IFactoryFunctionRequest<any, any>) => (resourceId: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/countOpen/resource/${resourceId}`,
  })
}

const closeByResource = (axios: IFactoryFunctionRequest<any, any>) => (resourceId: string) => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/closeByResource/${resourceId}`,
  })
}

const countSoleEvaluatorByResource = (axios: IFactoryFunctionRequest<any, any>) => (resourceId: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/countSoleEvaluator/resource/${resourceId}`,
  })
}

const reassignSoleEvaluatorByResource = (axios: IFactoryFunctionRequest<any, any>) => (resourceId: string, replacementResourceIds: string[]) => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/reassignSoleEvaluator/resource/${resourceId}`,
    data: { replacementResourceIds },
  })
}

export default (axios: AxiosInstance) => ({
  showFillEvaluation: showFillEvaluation(useAxios(axios)),
  showEvaluationList: showEvaluationList(useAxios(axios)),
  updateEvaluation: updateEvaluation(useAxios(axios)),
  showFillEvaluationExternal: showFillEvaluationExternal(useAxios(axios)),
  retreiveCreateResources: retreiveCreateResources(useAxios(axios)),
  createEvaluations: createEvaluations(useAxios(axios)),
  duplicateEvaluation: duplicateEvaluation(useAxios(axios)),
  showEditEvaluation: showEditEvaluation(useAxios(axios)),
  showAverageSummary: showAverageSummary(useAxios(axios)),
  sendReminders: sendReminders(useAxios(axios)),
  sendBulkReminders: sendBulkReminders(useAxios(axios)),
  sendLinktoShared: sendLinktoShared(useAxios(axios)),
  exportEvaluationsToExel: exportEvaluationsToExel(useAxios(axios)),
  exportEvaluationsCountByPersonToExcel: exportEvaluationsCountByPersonToExcel(useAxios(axios)),
  retreiveAllQuestionIdAvailable: retreiveAllQuestionIdAvailable(useAxios(axios)),
  retreiveStats: retreiveStats(useAxios(axios)),
  removeFromTrainingPlan: removeFromTrainingPlan(useAxios(axios)),
  addToTrainingPlan: addToTrainingPlan(useAxios(axios)),
  reorderTrainingPlan: reorderTrainingPlan(useAxios(axios)),
  getLearningPathWishes: getLearningPathWishes(useAxios(axios)),
  addLearningPathWish: addLearningPathWish(useAxios(axios)),
  removeLearningPathWish: removeLearningPathWish(useAxios(axios)),
  giveupEvaluations: giveupEvaluations(useAxios(axios)),
  deleteEvaluations: deleteEvaluations(useAxios(axios)),
  undoGiveupEvaluations: undoGiveupEvaluations(useAxios(axios)),
  undoDeleteEvaluations: undoDeleteEvaluations(useAxios(axios)),
  downloadPDF: downloadPDF(useAxios(axios)),
  downloadMarkdown: downloadMarkdown(useAxios(axios)),
  detectPdfLanguages: detectPdfLanguages(useAxios(axios)),
  downloadTranslatedPDF: downloadTranslatedPDF(useAxios(axios)),

  globalSearch: globalSearch(useAxios(axios)),
  countOpenByResource: countOpenByResource(useAxios(axios)),
  closeByResource: closeByResource(useAxios(axios)),
  countSoleEvaluatorByResource: countSoleEvaluatorByResource(useAxios(axios)),
  reassignSoleEvaluatorByResource: reassignSoleEvaluatorByResource(useAxios(axios)),
})
